import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { IWalletCategory } from '~/@api/generated-api-types-user/Api'

import { Table, TableBody, TableHeader, TableRow } from '~/shadcn/ui/table'
import { Spinner } from '~/shadcn/ui/spinner'
import { getColumnFilterLength } from '~/@mid_ui/table/getColumnFilterLength'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { FilterToggleButton } from '~/@mid_ui/table/FiltersToggle'
import { PageContainer } from '~/@mid_ui/page-container/PageContainer'
import { FiltersContainer } from '~/@mid_ui/table/FiltersContainer'
import { PAGE_SIZE } from '~/@mid_query/utils/pagination.utils'
import { useUser } from '~/@biz_account/useUser/useUser'
import {
  projectsFilteredForCurrentTab,
  sortingBySelectedTab,
} from '../running-projects_filters/utils'
import {
  useRunningProjectsTable,
  type ProjectsTableMeta,
} from './useRunningProjectsTable'
import { RunningProjectsFilters } from '../running-projects_filters/RunningProjectsFilters'
import { getRunningProjectTabs } from '../running-projects_filters/getRunningProjectTabs'
import { useFilters } from '../running-projects_filters/useFilters'
import {
  ProjectsTab,
  ProjectsTabsContainer,
} from '../_projects_filtering/ProjectsTab'
import { useRunningProjectsQuery } from './useRunningProjectsQuery'
import { useFeeImmunity } from '~/@pages_investing/fee-immunity/useFeeImmunity'
import { formatFeeSaving } from '~/@pages_investing/fee-immunity/feeImmunityDisplay.utils'
import { useRevenueFee } from '~/@pages_investing/_utils/revenue-fee/useRevenueFee'
import { ProjectsTableHead } from '../_projects_ui/project-table-components/ProjectsTableHead'
import { ProjectsTableRow } from '../_projects_ui/project-table-components/ProjectsTableRow'
import { Button } from '~/shadcn/ui/button/button'

export function RunningProjectsTable() {
  const {
    selectedTab,
    setSelectedTab,

    isFilterOpen,
    toggleFilterOpen,

    sorting,
    setSorting,
    handleSortingChange,
  } = useFilters()

  const { data, isFetching: isLoading } = useRunningProjectsQuery()

  const projects = (() => data?.items ?? [])()

  const navigate = useNavigate()
  const { user: userWithMeta } = useUser()
  const isDesktop = useIsDesktop()
  const { hasFeeImmunity } = useFeeImmunity()
  const { fee } = useRevenueFee()

  const projectsTabs = (() => {
    const allCount = projects.length
    const recommendedCount = projects.filter(
      (project) => project.recommended
    ).length
    return getRunningProjectTabs(allCount, recommendedCount)
  })()

  const projectsForCurrentTab = useMemo(() => {
    return projectsFilteredForCurrentTab(selectedTab, projects)
  }, [selectedTab, projects])

  const table = useRunningProjectsTable(
    projectsForCurrentTab,
    {
      sorting,
      onSortingChange: handleSortingChange,
    },
    {
      userMeta: userWithMeta?.['meta'],
      investorWallet: userWithMeta?.['wallets'].find(
        ({ category }) => category === IWalletCategory.Investor
      ),
      hasFeeImmunity,
      formatFeeSaving: (yieldValue: number) => formatFeeSaving(yieldValue, fee),
    } as ProjectsTableMeta
  )

  const columnFiltersLength = getColumnFilterLength(table)
  const activeFiltersCount = columnFiltersLength
    ? ` (${columnFiltersLength})`
    : ''

  if (isLoading && projects.length === 0) {
    return (
      <div className="flex justify-center items-center py-4 md:py-7">
        <Spinner className="size-6" />
      </div>
    )
  }

  return (
    <>
      <ProjectsTabsContainer>
        <PageContainer className="flex items-center justify-between">
          <div className="flex overflow-y-auto">
            {projectsTabs.map((tab, index) => (
              <ProjectsTab
                key={`RunningProjectsTab-${index}`}
                isActive={selectedTab === index}
                onClick={() => {
                  setSelectedTab(index)

                  const _sorting = sortingBySelectedTab[index]
                  if (_sorting) {
                    setSorting(_sorting)
                  }
                }}
                {...tab}
              />
            ))}
          </div>

          {isDesktop && !!projectsForCurrentTab.length && (
            <FilterToggleButton
              toggleFilterOpen={toggleFilterOpen}
              isFilterOpen={isFilterOpen}
              activeFiltersCount={activeFiltersCount}
            />
          )}
        </PageContainer>
      </ProjectsTabsContainer>

      {!isDesktop && !!projectsForCurrentTab.length && (
        <div className="border-b border-border-strong">
          <div className="px-5 py-7">
            <FilterToggleButton
              toggleFilterOpen={toggleFilterOpen}
              isFilterOpen={isFilterOpen}
              activeFiltersCount={activeFiltersCount}
            />
          </div>
        </div>
      )}
      {isFilterOpen && (
        <FiltersContainer>
          <PageContainer>
            <RunningProjectsFilters table={table} />
          </PageContainer>
        </FiltersContainer>
      )}

      <PageContainer className="pb-15 md:pb-30">
        {projectsForCurrentTab.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow
                      key={`TableRow-${headerGroup.id}`}
                      className="border-b border-border">
                      {headerGroup.headers.map((header, index) => (
                        <ProjectsTableHead
                          key={`TableHead-${header.id}`}
                          header={header}
                          index={index}
                        />
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>

                <TableBody className="border-b">
                  {table.getRowModel().rows.map((row, index) => (
                    <ProjectsTableRow
                      key={`TableRow-${row.id}`}
                      row={row}
                      userWithMeta={userWithMeta}
                      navigate={navigate}
                      enableAdvancedStyles
                      rowIndex={index}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>

            {table.getCanNextPage() && (
              <div className="flex items-center justify-center pt-7 md:pt-10">
                <Button
                  variant="outline"
                  onClick={() =>
                    table.setPageSize((pageSize) => pageSize + PAGE_SIZE)
                  }>
                  Ukázať ďalšie príležitosti
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="mt-20 mb-10">Žiadne záznamy</div>
        )}
      </PageContainer>
    </>
  )
}
